import { useState } from "react";

import { useTranslation } from "@/i18n";
import { MSG } from "@ease/shared/i18n/message-keys";

interface FeatureFlag {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  overrides: { tenantId: string; tenantName: string; enabled: boolean }[];
}

export default function AdminFlagsPage() {
  const { t } = useTranslation();
  const [flags, setFlags] = useState<FeatureFlag[]>([
    { id: "1", name: "ai_assistant", description: t(MSG.admin.flags.descriptions.aiAssistant), enabled: false, overrides: [] },
    { id: "2", name: "webinar_system", description: t(MSG.admin.flags.descriptions.webinarSystem), enabled: false, overrides: [] },
    { id: "3", name: "mcp_server", description: t(MSG.admin.flags.descriptions.mcpServer), enabled: false, overrides: [] },
    { id: "4", name: "domain_purchasing", description: t(MSG.admin.flags.descriptions.domainPurchasing), enabled: false, overrides: [] },
    { id: "5", name: "advanced_analytics", description: t(MSG.admin.flags.descriptions.advancedAnalytics), enabled: false, overrides: [] },
  ]);
  const [showCreate, setShowCreate] = useState(false);

  const toggleFlag = (id: string) => {
    setFlags((prev) => prev.map((f) => f.id === id ? { ...f, enabled: !f.enabled } : f));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">{t(MSG.admin.flags.title)}</h1>
        <button onClick={() => setShowCreate(!showCreate)}
          className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary)]/90">
          {t(MSG.admin.flags.createFlag)}
        </button>
      </div>

      {showCreate && (
        <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] p-4 space-y-3">
          <input type="text" placeholder={t(MSG.admin.flags.flagNamePlaceholder)}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white" />
          <input type="text" placeholder={t(MSG.admin.flags.descriptionPlaceholder)}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white" />
          <div className="flex gap-2">
            <button className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm text-white hover:bg-[var(--color-primary)]/90">{t(MSG.admin.flags.create)}</button>
            <button onClick={() => setShowCreate(false)}
              className="rounded-md border border-[var(--color-border)] px-3 py-2 text-sm text-muted-foreground hover:bg-[var(--color-surface)]">{t(MSG.admin.flags.cancel)}</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {flags.map((flag) => (
          <div key={flag.id} className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-white font-mono text-sm">{flag.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{flag.description}</p>
              </div>
              <button
                onClick={() => toggleFlag(flag.id)}
                className={`h-6 w-11 rounded-full transition-colors ${flag.enabled ? "bg-[var(--color-success)]" : "bg-slate-700"}`}
              >
                <span className={`block h-5 w-5 rounded-full bg-background shadow transition-transform ${flag.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>
            {flag.overrides.length > 0 && (
              <div className="mt-3 border-t border-[var(--color-border)] pt-3">
                <p className="text-xs text-muted-foreground mb-2">{t(MSG.admin.flags.perTenantOverrides)}</p>
                {flag.overrides.map((o) => (
                  <div key={o.tenantId} className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{o.tenantName}</span>
                    <span className={o.enabled ? "text-[var(--color-success)]" : "text-[var(--color-destructive)]/80"}>
                      {o.enabled ? t(MSG.admin.flags.enabled) : t(MSG.admin.flags.disabled)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <button className="mt-2 text-xs text-[var(--color-primary)]/70 hover:underline">{t(MSG.admin.flags.addTenantOverride)}</button>
          </div>
        ))}
      </div>
    </div>
  );
}
